// @src/pre-deployment/src/components/VocabularyLearn.js v2.2
// - Replace alert with inline message bar (designrule)

import React, { useState, useEffect, useRef, useMemo } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { VocabularyTracking, ChapterProgress, ExposureTracking } from '../../data/tracking';
import { useVocabulary, useSessionProgressActions } from '../../data/useDataService';
import { TrackingV2Service } from '../../tracking/trackingService.js';
import './VocabularyLearn.css';

// COMPREHENSIVE HARDCODED BACKUP DATA - Moved outside to prevent re-renders
const hardcodedVocabularyData = [
    {
        id: 'vocab_001',
        korean: '병원',
        english: 'hospital',
        theme: 'medical',
        example_sentence: '병원에 가야 해요. (I need to go to the hospital.)',
        example_translation: 'I need to go to the hospital.'
    },
    {
        id: 'vocab_002',
        korean: '의사',
        english: 'doctor',
        theme: 'medical',
        example_sentence: '의사가 진찰해 주세요. (Please have the doctor examine you.)',
        example_translation: 'Please have the doctor examine you.'
    },
    {
        id: 'vocab_003',
        korean: '간호사',
        english: 'nurse',
        theme: 'medical',
        example_sentence: '간호사가 도와주세요. (Please help me, nurse.)',
        example_translation: 'Please help me, nurse.'
    },
    {
        id: 'vocab_004',
        korean: '약',
        english: 'medicine',
        theme: 'medical',
        example_sentence: '약을 먹어야 해요. (I need to take medicine.)',
        example_translation: 'I need to take medicine.'
    },
    {
        id: 'vocab_005',
        korean: '아픔',
        english: 'pain',
        theme: 'medical',
        example_sentence: '아픔이 심해요. (The pain is severe.)',
        example_translation: 'The pain is severe.'
    }
];

const VocabularyLearn = ({ _moduleKey, _moduleConfig, navigation, _modulesOrder, _moduleMap, level, chapter }) => {
    const { user } = useAuth();

    const sessionType = 'Learn';
    const moduleType = 'vocabulary';

    // Firestore toggle state - REMOVED (defaulting to best available source)
    // const [useFirestore, setUseFirestore] = useState(false);

    // Get vocabulary data from data service
    const {
        data: fetchedVocabularyData,
        loading: vocabularyLoading,
        error: vocabularyError
    } = useVocabulary(level, chapter, 'Korea_University');

    // Note: hardcodedVocabularyData backup is defined outside component (line 13-54) to prevent re-renders

    // Data source selection with comprehensive logging
    const { vocabularyData, dataSource } = useMemo(() => {
        // PRIORITIZE FIRESTORE DATA automatically
        if (fetchedVocabularyData && Array.isArray(fetchedVocabularyData) && fetchedVocabularyData.length > 0) {
            console.group('🔥 FIRESTORE VOCABULARY DATA LOADED');
            console.log('📊 Firestore data structure:', {
                isArray: Array.isArray(fetchedVocabularyData),
                length: fetchedVocabularyData.length,
                firstItem: fetchedVocabularyData[0],
                keys: fetchedVocabularyData[0] ? Object.keys(fetchedVocabularyData[0]) : []
            });
            console.log('📊 Raw Firestore data:', fetchedVocabularyData);
            console.groupEnd();
            return { vocabularyData: fetchedVocabularyData, dataSource: 'Firestore' };
        } else {
            console.group('💾 HARDCODED VOCABULARY BACKUP DATA LOADED');
            console.log('📊 Hardcoded data structure:', {
                isArray: Array.isArray(hardcodedVocabularyData),
                length: hardcodedVocabularyData.length,
                firstItem: hardcodedVocabularyData[0]
            });
            console.log('📊 Hardcoded data:', hardcodedVocabularyData);
            console.groupEnd();
            return { vocabularyData: hardcodedVocabularyData, dataSource: 'Hardcoded Backup' };
        }
    }, [fetchedVocabularyData]);

    // Game state
    const [gameState, setGameState] = useState('setup'); // setup, playing, completed
    const [currentMode, setCurrentMode] = useState('meaning'); // meaning, spelling
    const [currentTheme, setCurrentTheme] = useState('all');
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [currentOptions, setCurrentOptions] = useState([]); // Store current question options
    const [selectedAnswer, setSelectedAnswer] = useState('');
    const [userAnswer, setUserAnswer] = useState('');
    const [showResult, setShowResult] = useState(false);
    const [score, setScore] = useState({ correct: 0, total: 0 });
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [questionQueue, setQuestionQueue] = useState([]);
    const [wrongAnswers, setWrongAnswers] = useState([]);
    const [answered, setAnswered] = useState(false);
    const [isLastAnswerCorrect, setIsLastAnswerCorrect] = useState(false);
    const [inlineMessage, setInlineMessage] = useState(null);

    const answerInputRef = useRef(null);

    const {
        saveExerciseAttemptWithSession
    } = useSessionProgressActions(user?.uid, parseInt(level), parseInt(chapter), sessionType);

    useEffect(() => {
        setInlineMessage(null);
    }, [currentTheme]);

    const timerRef = useRef(null);

    // Initialize component
    useEffect(() => {
        console.group('📚 VOCABULARY: Component Initialization');
        console.log('📊 Level:', level, 'Chapter:', chapter);
        console.log('📊 Data source:', dataSource);
        console.log('📊 Vocabulary loading:', vocabularyLoading);
        console.log('📊 Vocabulary error:', vocabularyError);
        console.log('📊 Vocabulary data structure:', {
            isArray: Array.isArray(vocabularyData),
            length: vocabularyData?.length,
            firstItem: vocabularyData?.[0]
        });
        console.groupEnd();

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [vocabularyData, vocabularyLoading, vocabularyError, level, chapter, dataSource]);

    // Get current theme words - updated to use new data source
    const getCurrentThemeWords = () => {
        if (!vocabularyData || !Array.isArray(vocabularyData)) {
            console.warn('⚠️ VOCABULARY: No vocabulary data available');
            return [];
        }

        if (currentTheme === 'all') {
            return vocabularyData;
        }

        return vocabularyData.filter(word => word.theme === currentTheme);
    };

    // Get available themes from current data source
    const getAvailableThemesFromData = () => {
        if (!vocabularyData || !Array.isArray(vocabularyData)) {
            return ['medical']; // fallback
        }

        const themes = [...new Set(vocabularyData.map(word => word.theme))];
        return themes.filter(theme => theme && theme !== 'all');
    };

    // Shuffle array function
    const shuffleArray = (array) => {
        const newArray = [...array];
        for (let i = newArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
        }
        return newArray;
    };

    // FIX-069: Moved above startGame/loadNextQuestion for no-use-before-define
    const generateMeaningOptions = (question) => {
        if (!question) return [];
        const correctAnswer = question.english;
        const options = [correctAnswer];
        const allWords = getCurrentThemeWords();
        while (options.length < 4 && allWords.length >= 4) {
            const randomWord = allWords[Math.floor(Math.random() * allWords.length)];
            const randomAnswer = randomWord.english;
            if (!options.includes(randomAnswer)) options.push(randomAnswer);
        }
        return shuffleArray(options);
    };

    const showResults = () => {
        setGameState('completed');
        if (user?.uid && vocabularyData && vocabularyData.length > 0) {
            const markModuleCompleted = async () => {
                try {
                    await TrackingV2Service.updateModuleProgress(
                        user.uid, level, chapter, 'vocabularyLearn', {
                        completion: true,
                        completionTime: new Date().toISOString(),
                        lastWordSeen: vocabularyData[vocabularyData.length - 1]?.id || null,
                        totalWordsLearned: vocabularyData.length,
                        wordsCorrect: score.correct,
                        accuracy: score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0
                    }, { moduleType: 'vocabularyLearn', completionMethod: 'user_finished', finalScore: score, accuracy: score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0 });
                    console.log('✅ Module completion tracked successfully');
                } catch (error) {
                    console.error('❌ Failed to track module completion:', error);
                    ChapterProgress.updateModule(user.uid, level, chapter, 'vocabularyLearn', { completion: true, completion_time: new Date().toISOString(), last_word_seen: (Array.isArray(vocabularyData) && vocabularyData.length > 0 ? vocabularyData[vocabularyData.length - 1]?.id : null) || null });
                }
            };
            markModuleCompleted();
            const vocabIds = (Array.isArray(vocabularyData) ? vocabularyData : []).map(word => word.id).filter(Boolean);
            if (vocabIds.length > 0) {
                const markAllVocabExposed = async () => {
                    try {
                        for (const vocabId of vocabIds) {
                            await TrackingV2Service.markVocabularyExposed(user.uid, level, chapter, vocabId, 'vocabularyLearn', { exposureMethod: 'module_completion', exposedAt: new Date().toISOString(), moduleCompleted: true });
                        }
                        console.log('✅ All vocabulary marked as exposed successfully');
                    } catch (error) {
                        console.error('❌ Failed to mark vocabulary as exposed:', error);
                        ExposureTracking.markAllVocabExposed(user.uid, level, chapter, vocabIds);
                    }
                };
                markAllVocabExposed();
            }
        }
    };

    // Start game with new tracking system
    const startGame = async () => {
        console.log('🎮 Starting Korean Vocabulary Game for Level 2 Chapter 1');
        const words = getCurrentThemeWords();
        if (words.length === 0) {
            setInlineMessage('Please select a learning theme first!');
            return;
        }
        setInlineMessage(null);

        try {
            // Track module start with new tracking system
            if (user?.uid) {
                await TrackingV2Service.updateModuleProgress(user.uid, level, chapter, 'vocabularyLearn', {
                    userStarted: true,
                    startDate: new Date().toISOString(),
                    lastOpened: new Date().toISOString(),
                    interactionType: 'flashcard',
                    totalWords: words.length,
                    selectedTheme: currentTheme
                }, {
                    moduleType: 'vocabularyLearn',
                    interactionType: 'flashcard',
                    startMethod: 'user_initiated',
                    theme: currentTheme,
                    wordCount: words.length
                });

                console.log('✅ Module start tracked successfully');
            }
        } catch (error) {
            console.error('❌ Failed to track module start:', error);

            // Fallback to old tracking system
            if (user?.uid) {
                ChapterProgress.updateModule(user.uid, level, chapter, 'vocabularyLearn', {
                    user_started: true,
                    start_date: new Date().toISOString(),
                    last_opened: new Date().toISOString(),
                    interaction_type: 'flashcard'
                });
            }
        }

        setGameState('playing');
        setScore({ correct: 0, total: 0 });
        setCurrentQuestionIndex(0);
        setWrongAnswers([]);
        setAnswered(false);

        // Create question queue
        const shuffledWords = shuffleArray(words);
        setQuestionQueue(shuffledWords);

        // 立即加载第一题
        if (shuffledWords.length > 0) {
            const firstQuestion = shuffledWords[0];
            setQuestionQueue(shuffledWords.slice(1)); // Use slice instead of updating prev
            setCurrentQuestion(firstQuestion);
            setCurrentQuestionIndex(1);
            setAnswered(false);
            setIsLastAnswerCorrect(false);
            setShowResult(false);
            setSelectedAnswer('');
            setUserAnswer('');

            // Mark word as exposed
            if (user && firstQuestion.id) {
                VocabularyTracking.markExposed(
                    user.uid,
                    parseInt(level),
                    parseInt(chapter),
                    sessionType,
                    firstQuestion.id
                ).catch(error => {
                    console.error('✗ Failed to mark vocabulary as exposed:', error);
                });
            }

            // Generate options for meaning mode
            if (currentMode === 'meaning') {
                const options = generateMeaningOptions(firstQuestion);
                setCurrentOptions(options);
            }
        }
    };

    // Load next question
    const loadNextQuestion = () => {
        // If current question was wrong, add to wrong answers
        if (currentQuestion && answered && !isLastAnswerCorrect) {
            setWrongAnswers(prev => [...prev, currentQuestion]);
        }

        // If question queue is empty, check if there are wrong answers to repeat
        if (questionQueue.length === 0) {
            if (wrongAnswers.length > 0) {
                // Still have wrong answers to repeat
                setQuestionQueue(shuffleArray(wrongAnswers));
                setWrongAnswers([]);
                setCurrentQuestionIndex(0);
            } else {
                // All questions answered correctly
                showResults();
                return;
            }
        }

        const nextQuestion = questionQueue[0];
        setQuestionQueue(prev => prev.slice(1));
        setCurrentQuestion(nextQuestion);
        setCurrentQuestionIndex(prev => prev + 1);
        setAnswered(false);
        setIsLastAnswerCorrect(false);

        // Mark word as exposed
        if (user && nextQuestion.id) {
            VocabularyTracking.markExposed(
                user.uid,
                parseInt(level),
                parseInt(chapter),
                sessionType,
                nextQuestion.id
            ).catch(error => {
                console.error('✗ Failed to mark vocabulary as exposed:', error);
            });
        }

        // Generate options for meaning mode
        if (currentMode === 'meaning') {
            const options = generateMeaningOptions(nextQuestion);
            setCurrentOptions(options);
        }

        // Hide feedback
        setShowResult(false);
        setSelectedAnswer('');
        setUserAnswer('');

        // Focus input for spelling mode
        if (currentMode === 'spelling' && answerInputRef.current) {
            setTimeout(() => {
                answerInputRef.current?.focus();
            }, 100);
        }
    };

    // Handle option selection
    const handleOptionSelect = (selectedOption) => {
        if (answered) return;

        setAnswered(true);
        const correctAnswer = currentQuestion.english;
        const isCorrect = selectedOption === correctAnswer;
        setIsLastAnswerCorrect(isCorrect);

        // Update score
        if (isCorrect) {
            setScore(prev => ({ ...prev, correct: prev.correct + 1 }));
        }
        setScore(prev => ({ ...prev, total: prev.total + 1 }));

        setShowResult(true);

        // Generate content-linked ID
        const wordId = currentQuestion.id || `ch${chapter}_${String(currentQuestionIndex).padStart(3, '0')}`;

        // Save exercise attempt with new tracking system
        if (user && currentQuestion) {
            const trackQuestionAttempt = async () => {
                try {
                    // Mark vocabulary as exposed
                    await TrackingV2Service.markVocabularyExposed(
                        user.uid, level, chapter, wordId, 'vocabularyLearn', {
                        exposureMethod: 'flashcard_view',
                        exposedAt: new Date().toISOString()
                    }
                    );

                    // Add question attempt
                    await TrackingV2Service.addQuestionAttempt(
                        user.uid, level, chapter, 'vocabularyLearn', currentQuestionIndex, {
                        questionType: 'vocabulary_meaning',
                        userResponse: selectedOption,
                        correctResponse: correctAnswer,
                        isCorrect: isCorrect,
                        responseTime: Date.now() - (currentQuestion.startTime || Date.now()),
                        responseOptions: generateMeaningOptions(currentQuestion),
                        interactionType: 'multiple_choice',
                        theme: currentQuestion.theme || currentTheme
                    }, {
                        questionType: 'vocabulary_meaning',
                        interactionType: 'multiple_choice',
                        theme: currentQuestion.theme || currentTheme
                    }
                    );

                    // Update vocabulary mastery
                    await TrackingV2Service.updateVocabularyMastery(
                        user.uid, level, chapter, wordId, 'vocabularyLearn', {
                        isCorrect: isCorrect,
                        userResponse: selectedOption,
                        correctResponse: correctAnswer,
                        responseTime: Date.now() - (currentQuestion.startTime || Date.now()),
                        interactionType: 'multiple_choice',
                        theme: currentQuestion.theme || currentTheme
                    }, {
                        masteryType: 'vocabulary_meaning',
                        interactionType: 'multiple_choice',
                        theme: currentQuestion.theme || currentTheme
                    }
                    );

                    console.log('✅ Vocabulary attempt tracked successfully');

                } catch (error) {
                    console.error('❌ Failed to track vocabulary attempt:', error);

                    // Fallback to old tracking system
                    saveExerciseAttemptWithSession({
                        exercise_id: wordId,
                        module_type: moduleType,
                        exercise_number: currentQuestionIndex,
                        user_answer: selectedOption,
                        correct_answer: correctAnswer,
                        is_correct: isCorrect,
                        time_spent: Date.now() - (currentQuestion.startTime || Date.now())
                    }).catch(error => {
                        console.error('✗ Failed to save vocabulary exercise attempt:', error);
                    });

                    VocabularyTracking.updateMastery(
                        user.uid,
                        parseInt(level),
                        parseInt(chapter),
                        sessionType,
                        wordId,
                        {
                            korean: currentQuestion.korean,
                            english: currentQuestion.english,
                            is_correct: isCorrect,
                            test_type: currentMode
                        }
                    ).catch(error => {
                        console.error('✗ Failed to update vocabulary mastery:', error);
                    });
                }
            };

            // Execute tracking
            trackQuestionAttempt();
        }

        // Auto continue after delay
        setTimeout(() => {
            loadNextQuestion();
        }, 1500);
    };

    // Handle spelling answer submission
    const handleSpellingSubmit = () => {
        if (answered || !userAnswer.trim()) return;

        const isCorrect = userAnswer.trim() === currentQuestion.korean;
        setIsLastAnswerCorrect(isCorrect);
        setAnswered(true);

        // Update score
        if (isCorrect) {
            setScore(prev => ({ ...prev, correct: prev.correct + 1 }));
        }
        setScore(prev => ({ ...prev, total: prev.total + 1 }));

        setShowResult(true);

        // Generate content-linked ID
        const wordId = currentQuestion.id || `ch${chapter}_${String(currentQuestionIndex).padStart(3, '0')}`;

        // Save exercise attempt
        if (user && currentQuestion) {
            saveExerciseAttemptWithSession({
                exercise_id: wordId,
                module_type: moduleType,
                exercise_number: currentQuestionIndex,
                user_answer: userAnswer.trim(),
                correct_answer: currentQuestion.korean,
                is_correct: isCorrect,
                time_spent: Date.now() - (currentQuestion.startTime || Date.now())
            }).catch(error => {
                console.error('✗ Failed to save vocabulary exercise attempt:', error);
            });

            // Update mastery tracking
            VocabularyTracking.updateMastery(
                user.uid,
                parseInt(level),
                parseInt(chapter),
                sessionType,
                wordId,
                {
                    korean: currentQuestion.korean,
                    english: currentQuestion.english,
                    is_correct: isCorrect,
                    test_type: currentMode
                }
            ).catch(error => {
                console.error('✗ Failed to update vocabulary mastery:', error);
            });
        }

        // Auto continue after delay
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            loadNextQuestion();
        }, 1500);
    };

    // Handle key press for spelling mode
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && currentMode === 'spelling' && !answered) {
            handleSpellingSubmit();
        }
    };

    // Reset game
    const resetGame = () => {
        setGameState('setup');
        setCurrentQuestion(null);
        setSelectedAnswer('');
        setUserAnswer('');
        setShowResult(false);
        setScore({ correct: 0, total: 0 });
        setCurrentQuestionIndex(0);
        setQuestionQueue([]);
        setWrongAnswers([]);
        setAnswered(false);
    };


    return (
        <div className="vocabulary-feature-container">
            {inlineMessage && (
                <div className="module-info" style={{ fontSize: '13px', padding: '0.5rem 1rem', marginBottom: '0.5rem', background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                    <span>{inlineMessage}</span>
                    <button type="button" onClick={() => setInlineMessage(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#721c24' }} aria-label="Dismiss">×</button>
                </div>
            )}
            {/* Data Source Indicator */}
            <div className="module-data-source-toggle">
                <div className="data-source-indicator">
                    <span className={`source-badge ${dataSource.toLowerCase().replace(' ', '-')}`}>
                        {dataSource}
                    </span>
                </div>
            </div>

            {gameState === 'setup' && (
                <div className="setup-screen">
                    {/* Mode Selector */}
                    <div className="mode-selection">
                        <h3>Choose Learning Mode:</h3>
                        <div className="mode-options">
                            <label className="mode-radio">
                                <input
                                    type="radio"
                                    name="mode"
                                    value="meaning"
                                    checked={currentMode === 'meaning'}
                                    onChange={() => setCurrentMode('meaning')}
                                />
                                <span className="mode-label">Meaning Test</span>
                            </label>
                            <label className="mode-radio">
                                <input
                                    type="radio"
                                    name="mode"
                                    value="spelling"
                                    checked={currentMode === 'spelling'}
                                    onChange={() => setCurrentMode('spelling')}
                                />
                                <span className="mode-label">Spelling Test</span>
                            </label>
                        </div>
                    </div>

                    {/* Theme Selector */}
                    <div className="theme-selection">
                        <h3>Choose Learning Theme:</h3>
                        <div className="theme-options">
                            <label className="theme-checkbox">
                                <input
                                    type="checkbox"
                                    checked={currentTheme === 'all'}
                                    onChange={() => setCurrentTheme('all')}
                                />
                                <span className="theme-label">All Themes</span>
                            </label>
                            {getAvailableThemesFromData().map(theme => (
                                <label key={theme} className="theme-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={currentTheme === theme}
                                        onChange={() => setCurrentTheme(theme)}
                                    />
                                    <span className="theme-label">{theme}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="vocabulary-preview">
                        <div className="preview-content">
                            <span className="preview-title">Content Preview</span>
                            <span className="preview-words">
                                {getCurrentThemeWords().slice(0, 5).map((word, index) =>
                                    `${word.korean} - ${word.english}${index < 4 ? ' | ' : ''}`
                                ).join('')}
                            </span>
                        </div>
                    </div>

                    <button className="start-button" onClick={startGame}>
                        <i className="fas fa-play"></i>
                        Start Learning
                    </button>
                </div>
            )}

            {gameState === 'playing' && (
                <div className="game-screen">
                    {vocabularyLoading ? (
                        <div className="loading-message">
                            <h3>Loading vocabulary...</h3>
                            <p>Please wait while we prepare your vocabulary practice.</p>
                        </div>
                    ) : vocabularyError && dataSource === 'Firestore' ? (
                        <div className="error-message">
                            <h3>Error loading vocabulary</h3>
                            <p>{vocabularyError.message}</p>
                            <p>Falling back to backup data if available.</p>
                        </div>
                    ) : !currentQuestion ? (
                        <div className="no-data-message">
                            <h3>No vocabulary available</h3>
                            <p>There are no vocabulary words for this level and chapter from the selected source.</p>
                        </div>
                    ) : (
                        <>
                            <div className="game-header">
                                <div className="score-display">
                                    Score: {score.correct}/{score.total}
                                    {score.total > 0 && (
                                        <span className="accuracy">
                                            ({((score.correct / score.total) * 100).toFixed(1)}%)
                                        </span>
                                    )}
                                </div>

                                <div className="progress-display">
                                    Question {currentQuestionIndex} / {getCurrentThemeWords().length}
                                </div>

                                <div className="progress-bar">
                                    <div
                                        className="progress-fill"
                                        style={{ width: `${(currentQuestionIndex / getCurrentThemeWords().length) * 100}%` }}
                                    ></div>
                                </div>
                            </div>

                            <div className="question-area">
                                <div className="question-header">
                                    <h3>
                                        {currentMode === 'meaning'
                                            ? 'What is the English meaning of this Korean word?'
                                            : 'Please enter the Korean word for this English meaning:'
                                        }
                                    </h3>
                                </div>

                                <div className="word-display">
                                    {currentMode === 'meaning' ? currentQuestion.korean : currentQuestion.english}
                                </div>

                                {currentMode === 'meaning' ? (
                                    <div className="options-container">
                                        {(Array.isArray(currentOptions) ? currentOptions : []).map((option, index) => (
                                            <button
                                                key={index}
                                                className={`option-btn ${selectedAnswer === option ? 'selected' : ''
                                                    } ${showResult && option === currentQuestion.english ? 'correct' : ''
                                                    } ${showResult && selectedAnswer === option && option !== currentQuestion.english ? 'incorrect' : ''
                                                    }`}
                                                onClick={() => handleOptionSelect(option)}
                                                disabled={answered}
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="input-container">
                                        <input
                                            ref={answerInputRef}
                                            type="text"
                                            value={userAnswer}
                                            onChange={(e) => setUserAnswer(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Enter Korean word..."
                                            disabled={answered}
                                            className="answer-input"
                                        />
                                        <button
                                            className="submit-button"
                                            onClick={handleSpellingSubmit}
                                            disabled={!userAnswer.trim() || answered}
                                        >
                                            Submit
                                        </button>
                                    </div>
                                )}

                                {showResult && (
                                    <div className="feedback">
                                        {isLastAnswerCorrect ? (
                                            <div className="correct-result">
                                                <i className="fas fa-check-circle"></i>
                                                Correct!
                                            </div>
                                        ) : (
                                            <div className="incorrect-result">
                                                <i className="fas fa-times-circle"></i>
                                                Wrong. The correct answer is:
                                                <strong>{currentMode === 'meaning' ? currentQuestion.english : currentQuestion.korean}</strong>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            {gameState === 'completed' && (
                <div className="completion-screen">
                    <h2>Learning Complete!</h2>

                    <div className="final-results">
                        <div className="result-card">
                            <h3>Final Score</h3>
                            <div className="score-large">
                                {score.correct} / {score.total}
                            </div>
                            <div className="accuracy-large">
                                {((score.correct / score.total) * 100).toFixed(1)}% Accuracy
                            </div>
                        </div>

                        <div className="result-card">
                            <h3>Learning Details</h3>
                            <ul>
                                <li>Learning Mode: {currentMode === 'meaning' ? 'Meaning Test' : 'Spelling Test'}</li>
                                <li>Language Mode: English</li>
                                <li>Learning Theme: {currentTheme === 'all' ? 'All Themes' : currentTheme}</li>
                                <li>Correct Answers: {score.correct}</li>
                                <li>Total Questions: {score.total}</li>
                                <li>Accuracy: {((score.correct / score.total) * 100).toFixed(1)}%</li>
                            </ul>
                        </div>
                    </div>

                    <div className="completion-actions">
                        <button onClick={() => navigation?.next?.()} className="next-button">
                            <i className="fas fa-arrow-right"></i>
                            Next Module
                        </button>
                        <button onClick={resetGame} className="setup-button">
                            <i className="fas fa-cog"></i>
                            Change Settings
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VocabularyLearn;
